/* Bella Ballou: produktinfo-faner (snippets/bb-info-tabs.liquid).
   WAI-ARIA tabs (automatisk aktivering, roving tabindex, venstre/højre/Home/End),
   "Læs mere" kun når indholdet faktisk er længere end --bb-info-tabs-clamp linjer,
   og en let oprydning af rodet produktbeskrivelses-HTML (tomme afsnit, afsluttende <br>, versal-overskrifter). */
(() => {
  if (customElements.get('bb-info-tabs')) return;

  const norm = (text) => (text || '').replace(/ /g, ' ').replace(/\s+/g, ' ').trim();
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  class BbInfoTabs extends HTMLElement {
    connectedCallback() {
      if (this.ready) {
        this.observeWidth();
        this.measureAll();
        return;
      }
      this.ready = true;

      this.readMore = this.dataset.readMore || 'Læs mere';
      this.readLess = this.dataset.readLess || 'Læs mindre';
      // Kun egne elementer: indsat HTML (fx butikkens politikker) kan selv indeholde role="tab"
      this.list = this.querySelector(':scope > .bb-info-tabs__list');
      this.tabs = this.list ? Array.from(this.list.querySelectorAll(':scope > .bb-info-tabs__tab')) : [];
      this.panels = Array.from(this.querySelectorAll(':scope > .bb-info-tabs__panel'));

      this.panels.forEach((panel) => {
        this.tidy(panel.querySelector('.bb-info-tabs__content'));
        const toggle = panel.querySelector('.bb-info-tabs__toggle');
        if (toggle) toggle.addEventListener('click', () => this.toggle(panel));
      });

      if (this.list && this.tabs.length > 1) {
        this.panels.forEach((panel) => {
          panel.setAttribute('role', 'tabpanel');
          panel.tabIndex = 0;
        });
        this.tabs.forEach((tab) => tab.addEventListener('click', () => this.select(tab, false)));
        this.list.addEventListener('keydown', (event) => this.onKeydown(event));
        this.list.hidden = false;
        this.classList.add('is-enhanced');
        const initial = this.tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || this.tabs[0];
        this.select(initial, false);
      } else {
        this.classList.add('is-enhanced');
        this.measureAll();
      }

      this.observeWidth();
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => this.measureAll());
    }

    disconnectedCallback() {
      if (this.resizeObserver) this.resizeObserver.disconnect();
    }

    observeWidth() {
      if (!('ResizeObserver' in window)) return;
      if (!this.resizeObserver) {
        this.lastWidth = this.clientWidth;
        this.resizeObserver = new ResizeObserver(() => {
          if (this.clientWidth === this.lastWidth) return;
          this.lastWidth = this.clientWidth;
          this.measureAll();
        });
      }
      this.resizeObserver.observe(this);
    }

    panelFor(tab) {
      return this.querySelector(`#${CSS.escape(tab.getAttribute('aria-controls'))}`);
    }

    select(tab, focus) {
      this.tabs.forEach((candidate) => {
        const active = candidate === tab;
        candidate.setAttribute('aria-selected', active ? 'true' : 'false');
        candidate.tabIndex = active ? 0 : -1;
        const panel = this.panelFor(candidate);
        if (panel) panel.hidden = !active;
      });
      if (focus) tab.focus({ preventScroll: true });
      this.scrollTabIntoView(tab);
      const panel = this.panelFor(tab);
      if (panel) this.measure(panel);
    }

    onKeydown(event) {
      const index = this.tabs.indexOf(event.target.closest('[role="tab"]'));
      if (index < 0) return;
      const last = this.tabs.length - 1;
      let next;
      switch (event.key) {
        case 'ArrowRight':
          next = index === last ? 0 : index + 1;
          break;
        case 'ArrowLeft':
          next = index === 0 ? last : index - 1;
          break;
        case 'Home':
          next = 0;
          break;
        case 'End':
          next = last;
          break;
        default:
          return;
      }
      event.preventDefault();
      this.select(this.tabs[next], true);
    }

    scrollTabIntoView(tab) {
      const list = this.list;
      if (!list || list.scrollWidth <= list.clientWidth) return;
      const listRect = list.getBoundingClientRect();
      const tabRect = tab.getBoundingClientRect();
      let delta = 0;
      if (tabRect.left < listRect.left) delta = tabRect.left - listRect.left;
      else if (tabRect.right > listRect.right) delta = tabRect.right - listRect.right;
      if (delta) list.scrollBy({ left: delta, behavior: reducedMotion() ? 'auto' : 'smooth' });
    }

    measureAll() {
      this.panels.forEach((panel) => this.measure(panel));
    }

    measure(panel) {
      const content = panel.querySelector('.bb-info-tabs__content');
      const toggle = panel.querySelector('.bb-info-tabs__toggle');
      if (!content || !toggle || panel.hidden || panel.dataset.expanded === 'true') return;
      content.classList.add('is-clamped');
      const overflows = content.scrollHeight - content.clientHeight > 2;
      content.classList.toggle('is-clamped', overflows);
      toggle.hidden = !overflows;
    }

    toggle(panel) {
      const content = panel.querySelector('.bb-info-tabs__content');
      const toggle = panel.querySelector('.bb-info-tabs__toggle');
      const expand = panel.dataset.expanded !== 'true';
      panel.dataset.expanded = expand ? 'true' : 'false';
      toggle.setAttribute('aria-expanded', expand ? 'true' : 'false');
      toggle.textContent = expand ? this.readLess : this.readMore;
      if (expand) {
        content.classList.remove('is-clamped');
      } else {
        this.measure(panel);
        if (panel.getBoundingClientRect().top < 0) {
          panel.scrollIntoView({ block: 'start', behavior: reducedMotion() ? 'auto' : 'smooth' });
        }
      }
    }

    tidy(root) {
      if (!root) return;

      // Indsat HTML må ikke bringe egne ARIA-roller/fokusrækkefølge med (fx jQuery UI-accordion i politikkerne)
      root.querySelectorAll('[role], [tabindex], [aria-controls], [aria-selected], [aria-expanded], [aria-hidden]').forEach((el) => {
        ['role', 'tabindex', 'aria-controls', 'aria-selected', 'aria-expanded', 'aria-hidden'].forEach((name) => el.removeAttribute(name));
      });
      root.querySelectorAll('meta, style, script').forEach((el) => el.remove());

      // Afsluttende <br> i afsnit/listepunkter giver en tom ekstra linje
      root.querySelectorAll('br').forEach((br) => {
        let next = br.nextSibling;
        while (next && next.nodeType === Node.TEXT_NODE && !norm(next.textContent)) next = next.nextSibling;
        if (!next) br.remove();
      });

      root.querySelectorAll('p, li').forEach((el) => {
        const text = norm(el.textContent);

        // Tomme afsnit (&nbsp;, tomme spans)
        if (!text) {
          if (!el.querySelector('img, iframe, video, svg')) el.remove();
          return;
        }

        if (el.tagName !== 'P') return;

        // Afsnit der kun består af fed tekst ("DANSK DESIGN") bliver til en lille versal-label
        const strong = el.querySelector('strong, b');
        if (strong && text.length <= 60 && norm(strong.textContent) === text) {
          el.classList.add('bb-info-tabs__label');
          return;
        }

        // Afsnit der kun består af kursiv ("Leveres i en eksklusiv gaveæske.") bliver dæmpet brødtekst
        const italicText = Array.from(el.querySelectorAll('em, i'))
          .filter((node) => !node.parentElement.closest('em, i') || node.parentElement === el)
          .map((node) => node.textContent)
          .join('');
        if (italicText && norm(italicText) === text) el.classList.add('bb-info-tabs__note');
      });
    }
  }

  customElements.define('bb-info-tabs', BbInfoTabs);
})();
